import React from 'react';
import { useAuth } from '@clerk/clerk-react';

/**
 * StudioIQ Cloudflare Workers API Service
 * Handles communication with parse-job, quote-job, and auth-check Workers
 * All endpoints require Clerk JWT authentication with 'studioiq-worker' template
 */

// Helper function to detect if running on localhost
function isLocalhost() {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname === '';
}

// Helper function to get endpoint URL dynamically
function getEndpointUrl(endpoint) {
  const isLocal = isLocalhost();
  
  const endpoints = {
    parseJob: process.env.REACT_APP_RUBY_WORKER_BASE_URL,
    quoteJob: process.env.REACT_APP_LOGIC_WORKER_BASE_URL,
    authCheck: process.env.REACT_APP_WORKER_BASE_URL,
    webhook: null
  };
  
  return endpoints[endpoint];
}

// Worker endpoint URLs - kept for backward compatibility
const WORKER_ENDPOINTS = {
  get parseJob() {
    return getEndpointUrl('parseJob');
  },
  get quoteJob() {
    return getEndpointUrl('quoteJob');
  },
  get authCheck() {
    return getEndpointUrl('authCheck');
  },
  get webhook() {
    return getEndpointUrl('webhook');
  }
};
 
// API configuration
const API_CONFIG = {
  timeout: 30000, // 30 seconds for AI processing
  retryAttempts: 3,
  retryDelay: 1000, // 1 second base delay
  template: 'studioiq-worker'
};

/**
 * Enhanced error class for StudioIQ API errors
 */
class StudioIQAPIError extends Error {
  constructor(message, status, endpoint, originalError = null) {
    super(message);
    this.name = 'StudioIQAPIError';
    this.status = status;
    this.endpoint = endpoint;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * StudioIQ API Client Class
 */
class StudioIQAPI {
  constructor() {
    this.authToken = null;
    this.tokenExpiryTime = null;
    this.getAuthToken = null; // Will be set by useStudioIQAPI hook
  }

  /**
   * Set the Clerk authentication function
   */
  setAuthFunction(getToken) {
    this.getAuthToken = getToken;
  }

  /**
   * Get or refresh authentication token
   */
  async getToken() {
    try {
      if (!this.getAuthToken) {
        throw new StudioIQAPIError('Authentication not initialized', 401, 'auth');
      }

      // Check if token is still valid (with 5 minute buffer)
      const now = Date.now();
      if (this.authToken && this.tokenExpiryTime && now < (this.tokenExpiryTime - 300000)) {
        return this.authToken;
      }

      // Get new token from Clerk
      const token = await this.getAuthToken({ template: API_CONFIG.template });
      
      if (!token) {
        throw new StudioIQAPIError('Failed to obtain authentication token', 401, 'auth');
      }

      this.authToken = token;
      // JWT tokens typically expire in 1 hour, set expiry time
      this.tokenExpiryTime = now + (55 * 60 * 1000); // 55 minutes

      return token;
    } catch (error) {
      console.error('Token acquisition failed:', error);
      throw new StudioIQAPIError(
        'Authentication failed - please sign in again',
        401,
        'auth',
        error
      );
    }
  }

  /**
   * Make authenticated API request with retry logic
   */
  async makeRequest(endpoint, method = 'GET', data = null, retryCount = 0) {
    try {
      const token = await this.getToken();
      const url = getEndpointUrl(endpoint);

      if (!url) {
        throw new StudioIQAPIError(`Unknown endpoint: ${endpoint}`, 400, endpoint);
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const requestOptions = {
        method,
        headers,
        ...(data && { body: JSON.stringify(data) })
      };

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

      try {
        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Handle non-200 responses
        if (!response.ok) {
          let errorMessage = `Request failed with status ${response.status}`;
          let responseData = null;

          try {
            responseData = await response.json();
            errorMessage = responseData.error || responseData.message || errorMessage;
          } catch (parseError) {
            // If response is not JSON, use status text
            errorMessage = response.statusText || errorMessage;
          }

          throw new StudioIQAPIError(errorMessage, response.status, endpoint);
        }

        // Parse successful response
        const responseData = await response.json();
        return responseData;

      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new StudioIQAPIError(`Request timeout after ${API_CONFIG.timeout}ms`, 408, endpoint);
        }
        
        throw fetchError;
      }

    } catch (error) {
      // Handle authentication errors
      if (error.status === 401 && retryCount === 0) {
        // Clear cached token and retry once
        this.authToken = null;
        this.tokenExpiryTime = null;
        return this.makeRequest(endpoint, method, data, retryCount + 1);
      }

      // Handle network errors with retry
      if (retryCount < API_CONFIG.retryAttempts && this.shouldRetry(error)) {
        const delay = API_CONFIG.retryDelay * Math.pow(2, retryCount); // Exponential backoff
        console.warn(`Request failed, retrying in ${delay}ms... (attempt ${retryCount + 1})`);
        await sleep(delay);
        return this.makeRequest(endpoint, method, data, retryCount + 1);
      }

      // Re-throw StudioIQ errors as-is
      if (error instanceof StudioIQAPIError) {
        throw error;
      }

      // Wrap other errors
      throw new StudioIQAPIError(
        error.message || 'Network error occurred',
        error.status || 500,
        endpoint,
        error
      );
    }
  }

  /**
   * Determine if error should trigger retry
   */
  shouldRetry(error) {
    // Retry on network errors, timeouts, and 5xx server errors
    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') return true;
    if (error.status === 408 || error.status === 429) return true; // Timeout or rate limit
    if (error.status >= 500 && error.status < 600) return true; // Server errors
    return false;
  }

  /**
   * Parse Job - Ruby Mode AI Processing
   * Accepts freeform input and returns either clarification questions or structured JSON
   */
  async parseJob(input, conversationHistory = [], customerContext = {}) {
    try {
      const payload = {
        input: input.trim(),
        conversationHistory,
        customerContext: {
          customerName: customerContext.customerName || '',
          jobId: customerContext.jobId || '',
          itemDescription: customerContext.itemDescription || ''
        }
      };

      const result = await this.makeRequest('parseJob', 'POST', payload);

      // Validate response structure
      if (!result || typeof result !== 'object') {
        throw new StudioIQAPIError('Invalid response format from parse-job', 500, 'parseJob');
      }

      // Expected response format:
      // { clarificationText: string, nextAction: "Manual Entry" | "Send to Quote Engine", structuredJob?: object }
      return {
        clarificationText: result.clarificationText || null,
        nextAction: result.nextAction || 'Manual Entry',
        structuredJob: result.structuredJob || null,
        success: true
      };

    } catch (error) {
      console.error('Parse job failed:', error);
      throw error instanceof StudioIQAPIError ? error : 
        new StudioIQAPIError('Failed to parse job input', 500, 'parseJob', error);
    }
  }

  /**
   * Quote Job - Generate formatted quote from structured job data
   * Accepts structured job object and returns formatted quote string
   * Routes through auth-check which forwards to logic-worker with correct authentication
   */
  async quoteJob(structuredJob) {
    try {
      if (!structuredJob || typeof structuredJob !== 'object') {
        throw new StudioIQAPIError('Invalid structured job data provided', 400, 'quoteJob');
      }

      // Route through auth-check (which forwards to logic-worker with PRICING_AUTH_TOKEN)
      // This ensures consistent authentication in both local and production
      const result = await this.makeRequest('authCheck', 'POST', { job: structuredJob });

      // Validate response structure
      if (!result || !result.formattedQuote) {
        throw new StudioIQAPIError('Invalid response format from quote-job', 500, 'quoteJob');
      }

      // Expected response format:
      // { formattedQuote: string, totalPrice?: number, timeEstimate?: string }
      return {
        formattedQuote: result.formattedQuote,
        totalPrice: result.totalPrice || null,
        timeEstimate: result.timeEstimate || null, // For internal use in Phase 2
        success: true
      };

    } catch (error) {
      console.error('Quote job failed:', error);
      throw error instanceof StudioIQAPIError ? error :
        new StudioIQAPIError('Failed to generate quote', 500, 'quoteJob', error);
    }
  }

  /**
   * Auth Check - Verify authentication and user permissions
   */
  async authCheck() {
    try {
      const result = await this.makeRequest('authCheck', 'GET');

      return {
        authenticated: result.authenticated || false,
        userId: result.userId || null,
        plan: result.plan || null,
        storeId: result.storeId || null,
        success: true
      };

    } catch (error) {
      console.error('Auth check failed:', error);
      throw error instanceof StudioIQAPIError ? error :
        new StudioIQAPIError('Authentication check failed', 500, 'authCheck', error);
    }
  }
/**
 * Search Repairs - Manual mode repair database search
 * Returns list of repairs matching search criteria
 */
async searchRepairs(query = '', limit = 20) {
  try {
    // Build query parameters
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString()
    });

    // Use auth-check endpoint with repairs path
    const token = await this.getToken();
    const url = `${WORKER_ENDPOINTS.authCheck}/repairs/search?${params}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new StudioIQAPIError('Failed to search repairs', response.status, 'searchRepairs');
    }

    const result = await response.json();
    
    return {
      repairs: result.repairs || [],
      success: result.success || false
    };

  } catch (error) {
    console.error('Search repairs failed:', error);
    throw error instanceof StudioIQAPIError ? error :
      new StudioIQAPIError('Failed to search repairs database', 500, 'searchRepairs', error);
  }
}
  /**
   * Webhook Stub - Placeholder for Phase 2 webhook integration
   * Will send parsed job JSON, quote output, and user/store ID to webhook endpoint
   */
  async sendWebhook(payload) {
    if (!WORKER_ENDPOINTS.webhook) {
      console.warn('Webhook endpoint not configured - skipping webhook send');
      return { success: false, message: 'Webhook not configured' };
    }

    try {
      // Future implementation for Phase 2
      // const webhookPayload = {
      //   parsedJob: payload.parsedJob,
      //   quoteOutput: payload.quoteOutput,
      //   userId: payload.userId,
      //   storeId: payload.storeId,
      //   timestamp: new Date().toISOString()
      // };

      console.log('Webhook stub called with payload:', payload);
      return { success: true, message: 'Webhook stub - implementation pending' };

    } catch (error) {
      console.error('Webhook send failed:', error);
      throw new StudioIQAPIError('Webhook delivery failed', 500, 'webhook', error);
    }
  }

  /**
   * Get Repairs Schema - For Manual Mode form initialization
   * Returns all jewelry types, repair categories, and field requirements
   */
  async getRepairsSchema() {
    try {
      const token = await this.getToken();
      const url = `${WORKER_ENDPOINTS.parseJob}/repairs/schema`;

      console.log('[API] Environment:', isLocalhost() ? 'localhost' : 'production');
      console.log('[API] Getting schema from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new StudioIQAPIError('Failed to get schema', response.status, 'getRepairsSchema');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Get schema failed:', error);
      throw error instanceof StudioIQAPIError ? error :
        new StudioIQAPIError('Failed to get repairs schema', 500, 'getRepairsSchema', error);
    }
  }

  /**
   * Get Field Options - For Manual Mode dynamic dropdowns
   * Returns options for a specific field filtered by previous selections
   */
  async getFieldOptions(params) {
    try {
      const { category, jewelryType, field, subtype, metalType, sizeInfo, search } = params;
      
      const queryParams = new URLSearchParams({
        category,
        jewelryType,
        field
      });
      
      if (subtype) queryParams.append('subtype', subtype);
      if (metalType) queryParams.append('metalType', metalType);
      if (sizeInfo) queryParams.append('sizeInfo', sizeInfo);
      if (search) queryParams.append('search', search);

      const token = await this.getToken();
      const url = `${WORKER_ENDPOINTS.parseJob}/repairs/options?${queryParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new StudioIQAPIError('Failed to get field options', response.status, 'getFieldOptions');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Get field options failed:', error);
      throw error instanceof StudioIQAPIError ? error :
        new StudioIQAPIError('Failed to get field options', 500, 'getFieldOptions', error);
    }
  }

  /**
   * Search Repairs - For Manual Mode dropdown selection
   * Returns available repairs from the database schema
   */
  async searchRepairs(query = '', limit = 30) {
    try {
      // For now, return mock data based on REPAIRS_DB_SCHEMA
      // In production, this would query a real database
      const mockRepairs = [
        {
          id: 'SZ001',
          name: 'Ring Sizing (Up)',
          category: 'Sizing',
          jewelryType: 'Ring',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 1,
          description: 'Increase ring size by up to 2 sizes'
        },
        {
          id: 'SZ002', 
          name: 'Ring Sizing (Down)',
          category: 'Sizing',
          jewelryType: 'Ring',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 1,
          description: 'Decrease ring size by up to 2 sizes'
        },
        {
          id: 'PR001',
          name: 'Prong Retipping (Single)',
          category: 'Prong Work',
          jewelryType: 'Ring',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 2,
          description: 'Replace single worn prong'
        },
        {
          id: 'PR002',
          name: 'Prong Retipping (Multiple)',
          category: 'Prong Work',
          jewelryType: 'Ring',
          metalType: 'Gold', 
          metalTier: '14K',
          complexityTier: 3,
          description: 'Replace 2-4 worn prongs'
        },
        {
          id: 'SS001',
          name: 'Stone Setting (Center)',
          category: 'Stone Setting',
          jewelryType: 'Ring',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 3,
          description: 'Set center stone in mounting'
        },
        {
          id: 'PL001',
          name: 'Polish & Clean (Basic)',
          category: 'Polishing',
          jewelryType: 'Ring',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 1,
          description: 'Basic polish and ultrasonic clean'
        },
        {
          id: 'CH001',
          name: 'Chain Link Repair',
          category: 'Chain Repair',
          jewelryType: 'Necklace',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 2,
          description: 'Repair broken chain link'
        },
        {
          id: 'CL001',
          name: 'Clasp Replacement',
          category: 'Clasp Repair',
          jewelryType: 'Necklace',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 2,
          description: 'Replace broken or worn clasp'
        },
        {
          id: 'WB001',
          name: 'Watch Battery Replacement',
          category: 'Battery Replacement',
          jewelryType: 'Watch',
          metalType: 'Stainless Steel',
          metalTier: 'Standard',
          complexityTier: 1,
          description: 'Replace watch battery with seal test'
        },
        {
          id: 'EN001',
          name: 'Custom Engraving',
          category: 'Engraving',
          jewelryType: 'Ring',
          metalType: 'Gold',
          metalTier: '14K',
          complexityTier: 2,
          description: 'Custom text engraving inside band'
        }
      ];

      // Filter by search query if provided
      let filteredRepairs = mockRepairs;
      if (query && query.trim()) {
        const searchLower = query.toLowerCase();
        filteredRepairs = mockRepairs.filter(repair => 
          repair.name.toLowerCase().includes(searchLower) ||
          repair.category.toLowerCase().includes(searchLower) ||
          repair.jewelryType.toLowerCase().includes(searchLower) ||
          repair.description.toLowerCase().includes(searchLower)
        );
      }

      // Apply limit
      const limitedRepairs = filteredRepairs.slice(0, limit);

      return {
        repairs: limitedRepairs,
        total: filteredRepairs.length,
        query: query,
        success: true
      };

    } catch (error) {
      console.error('Search repairs failed:', error);
      throw new StudioIQAPIError('Failed to search repairs', 500, 'searchRepairs', error);
    }
  }

  /**
   * Health check for all Worker endpoints
   */
  async healthCheck() {
    try {
      const authResult = await this.authCheck();
      return {
        healthy: authResult.success,
        timestamp: new Date().toISOString(),
        endpoints: Object.keys(WORKER_ENDPOINTS).filter(key => key !== 'webhook')
      };
    } catch (error) {
      return {
        healthy: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Create singleton instance
const studioiqAPI = new StudioIQAPI();

/**
 * React hook to use StudioIQ API with Clerk authentication
 */
export const useStudioIQAPI = () => {
  const { getToken } = useAuth();

  // Initialize the API with Clerk's getToken function
  React.useEffect(() => {
    if (getToken) {
      studioiqAPI.setAuthFunction(getToken);
    }
  }, [getToken]);

  return studioiqAPI;
};

// Manual Mode - REPAIRS_DB Schema constants for UI building
export const REPAIRS_DB_SCHEMA = {
  jewelryTypes: [
    'Ring', 'Necklace', 'Bracelet', 'Earrings', 'Watch', 'Pendant', 'Brooch', 'Other'
  ],
  repairTypes: [
    'Sizing', 'Prong Work', 'Stone Setting', 'Polishing', 'Chain Repair', 'Clasp Repair', 
    'Battery Replacement', 'Crystal Replacement', 'Engraving', 'Soldering', 'Other'
  ],
  metalTypes: [
    'Gold', 'Silver', 'Platinum', 'Palladium', 'Stainless Steel', 'Titanium', 'Other'
  ],
  metalColors: [
    'Yellow', 'White', 'Rose', 'Two-Tone', 'Tri-Color', 'Natural', 'Other'
  ],
  metalTiers: [
    '10K', '14K', '18K', '22K', '24K', 'Sterling Silver', 'Fine Silver', 'Platinum 950', 'Other'
  ],
  complexityTiers: [
    'Simple', 'Moderate', 'Complex', 'Expert Level'
  ]
};

export default studioiqAPI;
export { StudioIQAPIError };